import { UseUserPhoneNumberFormProps } from './props';
import { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';

import { useCurrentModal } from '../../modals/useCurrentModal';
import { useModal } from '../../modals';
import { CommonModalsKey } from '../../../enums';
import { useDebounce } from '../../useDebounce';
import { useCountriesByOriginId } from '../../countries';
import {
  useCreatePhoneNumber,
  usePhoneNumberById,
  usePhoneNumbersLookup,
  useSendUserPhoneNumberCode,
  useUserPhoneNumberVerify,
} from '../../../api';
import { Either } from '../../../interfaces';
import {
  Country,
  GenericError,
  UserPhoneNumber,
  UserPhoneNumberFormProps,
} from '../../../models';
import { useLottiePlayer } from '../../useLottiePlayer';
import { useSearchParams } from '../../useSearchParams';
import { useNavigate } from '../../useNavigate';
import { NavigateFunction } from '@starsoft/common/interfaces/navigate-function';
import { Path, PathValue } from 'react-hook-form';

/**
 * Custom hook to manage the user phone number form.
 * This hook provides functionalities to handle phone number creation, verification, and form state management.
 *
 * @template T - Type of the form values.
 * @param {UseUserPhoneNumberFormProps<T>} props - Props to initialize the phone number form.
 * @param {Function} props.watch - Function to watch form values.
 * @param {Function} props.setValue - Function to set form values.
 * @param {boolean} [props.enabled=true] - Flag to indicate if the form is enabled.
 * @param {string} [props.countryQuery] - Query string to filter countries.
 * @returns {Object} An object containing various states and functions to manage the phone number form.
 *
 * @example
 * // Usage example:
 * const { onSubmit, isSubmitDisabled } = usePhoneNumberForm({
 *   watch: formWatch,
 *   setValue: formSetValue,
 *   enabled: true,
 *   countryQuery: 'Europe',
 * });
 *
 * // Call onSubmit with form values
 * onSubmit({
 *   countryId: 1,
 *   phoneNumber: '1234567890',
 *   code: '1234',
 *   phoneNumberId: '1',
 * });
 */
export function usePhoneNumberForm<T>({
  watch,
  setValue,
  enabled = true,
  countryQuery,
}: UseUserPhoneNumberFormProps<T>) {
  // Get the current modal and modal functions
  const { onClose } = useModal(CommonModalsKey.UserPhoneNumber);
  const modal = useCurrentModal(CommonModalsKey);

  // Get search parameters and navigate function
  const search = useSearchParams();
  const navigate: NavigateFunction = useNavigate();

  // Determine if the flow is for phone number creation
  const isCreationFlow = search.get('flow') == 'create';
  const kycStep = search.get('kyc-checkpoint-id');

  // Watch form values
  const phoneNumber = watch('phoneNumber' as Path<T>);
  const debouncedPhoneNumber = useDebounce(phoneNumber);
  const code = watch('code' as Path<T>);
  const countryId = watch('countryId' as Path<T>);
  const phoneNumberId = search.get('phone-number-id');
  //@ts-ignore
  const formCountry: Country = watch('country' as Path<T>);

  // Fetch countries based on origin ID
  const countriesQuery = useCountriesByOriginId({
    filterByOrigin: !!kycStep,
    query: countryQuery,
  });
  const {
    countries,
    error: errorCountries,
    refetch: refetchCountries,
    isLoading: isLoadingCountries,
  } = countriesQuery;

  // Fetch user phone number by ID
  const {
    userPhoneNumber,
    error: errorPhoneNumber,
    refetch: refetchPhoneNumber,
    isPending: isLoadingPhoneNumber,
  } = usePhoneNumberById({
    id: phoneNumberId ? Number(phoneNumberId) : undefined,
  });

  // Determine if the user phone number has been created
  const hasCreatedUserPhoneNumber =
    !!phoneNumberId && userPhoneNumber?.verified == false;

  // State to manage the selected country
  const [country, setCountry] = useState<Either<Country, undefined>>();
  const foundCountry: Either<Country, undefined> = useMemo(
    () => countries?.find(country => country?.id === countryId),
    [countries, countryId],
  );

  // Function to handle country change
  function onChangeCountry() {
    if (!foundCountry) {
      return;
    }

    setCountry(foundCountry);
  }

  // Effect to handle country change
  useEffect(onChangeCountry, [foundCountry]);

  // Send user phone number code
  const {
    sendUserPhoneNumberCode,
    isPending: isPendingSendUserPhoneNumberCode,
  } = useSendUserPhoneNumberCode();

  // Determine if the form is in skeleton loading state
  const isSkeleton: boolean =
    isLoadingCountries ??
    isLoadingPhoneNumber ??
    isPendingSendUserPhoneNumberCode;

  // Lookup phone numbers
  const {
    phoneNumbersLookup,
    isPending: isLoadingLookup,
    error: errorLookup,
    isSuccess,
  } = usePhoneNumbersLookup({
    enabled:
      !phoneNumberId &&
      typeof debouncedPhoneNumber === 'string' &&
      debouncedPhoneNumber.length >= 3 &&
      debouncedPhoneNumber.length <= 21,
    countryCode: country?.code ?? formCountry?.code,
    phoneNumber: debouncedPhoneNumber as string,
  });

  // Create phone number
  const { createPhoneNumber, isPending: isPendingCreatePhoneNumber } =
    useCreatePhoneNumber({
      onSuccess: (userPhoneNumber: UserPhoneNumber) => {
        setValue(
          'phoneNumberId' as Path<T>,
          userPhoneNumber.id as PathValue<T, Path<T>>,
        );
        handleUpdateUrlOnPhoneNumberCreation(userPhoneNumber);
      },
    });

  // Verify user phone number
  const { verifyUserPhoneNumber, isPending: isPendingVerifyUserPhoneNumber } =
    useUserPhoneNumberVerify({
      isCreating: isCreationFlow,
    });

  // Lottie animation reference
  const { lottieAnimationRef } = useLottiePlayer({
    path: '/phoneNumber.json',
    dependecyArray: [isLoadingCountries, isLoadingPhoneNumber],
  });

  // Error state
  const error: AxiosError<GenericError> = errorCountries ?? errorPhoneNumber;

  // Determine if the submit button should be disabled
  const isSubmitDisabled =
    (!hasCreatedUserPhoneNumber &&
      (!phoneNumber ||
        !countryId ||
        !phoneNumbersLookup?.valid ||
        isLoadingLookup ||
        Boolean(errorLookup))) ||
    (hasCreatedUserPhoneNumber && (!code || (code as string)?.length < 4));

  // Function to set masked phone number
  function setMaskedPhoneNumber() {
    if (
      !isSuccess ||
      !phoneNumbersLookup ||
      !phoneNumbersLookup?.valid ||
      !phoneNumbersLookup?.maskedPhoneNumber
    ) {
      return;
    }

    setValue(
      'phoneNumber' as Path<T>,
      phoneNumbersLookup?.maskedPhoneNumber as PathValue<T, Path<T>>,
    );
  }

  // Effect to set masked phone number
  useEffect(setMaskedPhoneNumber, [isSuccess, phoneNumbersLookup, setValue]);

  // Determine if there is an error in phone number lookup
  const isErrorPhoneNumberLookUp: boolean =
    (!!phoneNumbersLookup && !phoneNumbersLookup?.valid && !isLoadingLookup) ||
    (debouncedPhoneNumber as string)?.length < 3 ||
    (debouncedPhoneNumber as string)?.length > 21;

  // Function to handle URL update on phone number creation
  function handleUpdateUrlOnPhoneNumberCreation(data: UserPhoneNumber) {
    search.set('phone-number-id', String(data?.id));
    search.set('flow', 'create');
    navigate({
      search: search?.toString(),
      hash: modal,
    });
  }

  // Function to handle form submission
  function onSubmit({
    countryId,
    phoneNumber,
    code,
    phoneNumberId,
  }: UserPhoneNumberFormProps) {
    if (isSubmitDisabled) {
      onError();
      return;
    }

    if (!hasCreatedUserPhoneNumber) {
      createPhoneNumber({ countryId, phoneNumber });
      return;
    }

    verifyUserPhoneNumber({ code, phoneNumberId });
  }

  // Function to handle form submission error
  function onError() {
    // NotificationsService.error({
    //   title: 'form_error_title',
    //   message: 'form_error_description',
    // });
  }

  // Function to refetch data
  function refetch() {
    if (errorCountries) refetchCountries();
    if (errorPhoneNumber) refetchPhoneNumber();
  }

  // Function to fetch phone number
  function onFetchPhoneNumber() {
    if (!userPhoneNumber || !enabled) return;

    if (userPhoneNumber.verified && modal === CommonModalsKey.UserPhoneNumber) {
      onClose();
      return;
    }

    setValue(
      'phoneNumberId' as Path<T>,
      userPhoneNumber.id as PathValue<T, Path<T>>,
    );
    setValue(
      'phoneNumber' as Path<T>,
      userPhoneNumber.maskedPhoneNumber as PathValue<T, Path<T>>,
    );
    setValue(
      'countryId' as Path<T>,
      userPhoneNumber.country.id as PathValue<T, Path<T>>,
    );
    setValue(
      'country' as Path<T>,
      userPhoneNumber?.country as PathValue<T, Path<T>>,
    );

    if (!isCreationFlow) {
      sendUserPhoneNumberCode({
        phoneNumberId: userPhoneNumber.id?.toString(),
      });
      setCountry(userPhoneNumber?.country);
    }
  }

  // Effect to fetch phone number
  useEffect(onFetchPhoneNumber, [
    userPhoneNumber,
    enabled,
    modal,
    isCreationFlow,
    onClose,
    setValue,
    sendUserPhoneNumberCode,
  ]);

  // Function to set country on mount
  function handleSetCountryOnMount() {
    if (!kycStep || !countries || countries?.length > 1 || !!userPhoneNumber) {
      return;
    }

    setValue(
      'countryId' as Path<T>,
      countries?.[0]?.id as PathValue<T, Path<T>>,
    );
  }

  // Effect to set country on mount
  useEffect(handleSetCountryOnMount, [
    kycStep,
    countries,
    userPhoneNumber,
    setValue,
  ]);

  return {
    hasCreatedUserPhoneNumber,
    isSubmitDisabled,
    isSkeleton,
    error,
    lottieAnimationRef,
    onSubmit,
    refetch,
    isPendingCreatePhoneNumber,
    isPendingVerifyUserPhoneNumber,
    onError,
    isCreationFlow,
    country: country ?? formCountry,
    countriesQuery: {
      ...countriesQuery,
      countries,
      errorCountries,
      refetchCountries,
      isLoadingCountries,
    },
    phoneNumber,
    isErrorPhoneNumberLookUp,
  };
}

export type { UseUserPhoneNumberFormProps };
